/**
 * @module Shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import {
  AfterViewInit,
  Input,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
  Output,
  EventEmitter,
} from '@angular/core';

@Directive({
  selector: '[tmoDropDown]',
})
/*
 *  This directive will take pattern and accept only allowed characters as given in the pattern
 */
export class DropDownDirective implements AfterViewInit, OnDestroy {
  /*
   *  To hold the prefix value for id.
   */
  @Input() public prefix: string;
  /*
   *  To emit the changed value.
   */
  @Output() public onInputEvent: EventEmitter<string>;

  private focusCallBack: Function;
  private keyUpCallBack: Function;
  private clickCallBack: Function;
  private typedStr: string;
  private timeOutRef: number;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {
    this.onInputEvent = new EventEmitter<string>();
    this.typedStr = '';
    this.prefix = '';
    this.focusCallBack = () => {};
    this.keyUpCallBack = () => {};
    this.clickCallBack = () => {};
  }

  /**
   * Method to run after view rendered so that it can capture prime-ng elements
   * @return void
   */
  public ngAfterViewInit(): void {
    let options: HTMLOptionElement[];
    const inputElem = this.elRef.nativeElement.querySelector('input');
    const selectTag = this.elRef.nativeElement.querySelector('select');
    const outerDiv = this.elRef.nativeElement.querySelector('.p-dropdown');
    inputElem?.setAttribute('role', 'combobox');
    if (this.prefix && this.prefix.length) {
      outerDiv.setAttribute('id', this.prefix + '-dropdown');
    }
    if (inputElem) {
      this.focusCallBack = this.renderer.listen(inputElem, 'focus', () => {
        this.setExpandCollapse(inputElem);
        options = this.elRef.nativeElement.querySelectorAll('option');
        options = options.length === 0 ? [] : Array.prototype.slice.call(options);
      });

      this.keyUpCallBack = this.renderer.listen(inputElem, 'keyup', (evt: KeyboardEvent) => {
        const key = evt.key;
        const space = ' ';
        const enter = 'Enter';
        if (key === space || key === enter) {
          this.setExpandCollapse(inputElem);
        } else if (key && key.trim().length) {
          this.typedStr += key;
          this.handleKeyUp(selectTag, options);
        }
      });

      this.clickCallBack = this.renderer.listen(
        this.elRef.nativeElement,
        'click',
        (evt: MouseEvent) => {
          this.setExpandCollapse(inputElem);
        }
      );
    }
  }

  /**
   * ngOnDestroy method
   * @return void
   */
  public ngOnDestroy(): void {
    this.focusCallBack();
    this.keyUpCallBack();
    this.clickCallBack();
    this.typedStr = null;
  }

  /**
   * method to handle the keyup events on dropdown
   * @return void
   */
  private handleKeyUp(selectTag: HTMLSelectElement, options: HTMLOptionElement[]): void {
    let filteredOptions;
    const timeout = 600;
    filteredOptions = options.filter((option: HTMLOptionElement) => {
      const cond1 = option.innerText.toLowerCase().indexOf(this.typedStr.toLowerCase()) === 0;
      return cond1;
    });
    if (filteredOptions.length) {
      const ten: number = 10;
      let index = parseInt(selectTag.getAttribute('filter-index'), ten);
      if (selectTag.getAttribute('filter-char') !== this.typedStr) {
        index = 0;
      } else {
        index = index < filteredOptions.length - 1 ? index + 1 : 0;
      }
      selectTag.setAttribute('filter-char', this.typedStr);
      selectTag.setAttribute('filter-index', index.toString());
      this.onInputEvent.emit(filteredOptions[index].value);
    }
    if (this.timeOutRef) {
      clearTimeout(this.timeOutRef);
    }
    this.timeOutRef = window.setTimeout(() => {
      this.typedStr = '';
      const labelElem = this.elRef.nativeElement.querySelector('.p-dropdown-label');
      if (labelElem) {
        labelElem.setAttribute('aria-hidden', 'true');
      }
    }, timeout);
  }

  /**
   * method to set expand - collapsed for aria attribute
   * @return void
   */
  private setExpandCollapse(inputElem: HTMLInputElement): void {
    const openDiv = this.elRef.nativeElement.querySelector('.p-dropdown-open');
    if (openDiv) {
      inputElem.setAttribute('aria-expanded', 'true');
      this.addIdsToMenuItems();
    } else {
      inputElem.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * method to add ids on the list items of dropdown.
   * @return void
   */
  private addIdsToMenuItems(): void {
    const arrLiTags = this.elRef.nativeElement.querySelectorAll('li');
    arrLiTags.forEach((el: HTMLElement) => {
      const value = el.innerText;
      if (this.prefix && this.prefix.length) {
        el.setAttribute('id', this.prefix + '-' + value);
      }
    });
  }
}
